import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  LayoutGrid, 
  Grid, 
  Columns, 
  Rows, 
  Split, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Upload, 
  Trash2, 
  Image as ImageIcon, 
  Settings, 
  Sparkles, 
  Check, 
  AlertCircle, 
  Plus, 
  Sliders,
  Palette
} from 'lucide-react';

const LAYOUT_PRESETS = [
  { id: 'grid-2x2', name: 'Grid 2x2', slots: 4, icon: Grid },
  { id: 'grid-3x3', name: 'Grid 3x3', slots: 9, icon: LayoutGrid },
  { id: 'split-vertical', name: 'Vertical Split', slots: 2, icon: Columns },
  { id: 'split-horizontal', name: 'Horizontal Split', slots: 2, icon: Rows },
  { id: 'collage-3', name: 'Gallery 3', slots: 3, icon: Split },
  { id: 'hero-top', name: 'Hero Header', slots: 4, icon: LayoutGrid },
  { id: 'collage-5', name: 'Studio 5', slots: 5, icon: LayoutGrid },
];

const ASPECT_PRESETS = [
  { id: '1:1', label: 'Square (1:1)', value: '1/1', canvasW: 1200, canvasH: 1200 },
  { id: '16:9', label: 'Landscape (16:9)', value: '16/9', canvasW: 1600, canvasH: 900 },
  { id: '9:16', label: 'Story (9:16)', value: '9/16', canvasW: 900, canvasH: 1600 },
  { id: '4:3', label: 'Classic (4:3)', value: '4/3', canvasW: 1600, canvasH: 1200 },
];

const PRESET_COLORS = ['#ffffff', '#000000', '#0f172a', '#f8fafc', '#ec4899', '#3b82f6', '#10b981', '#8b5cf6'];

export const PhotoCollageMaker = () => {
  const { withAd, toast } = useAdRedirect();
  const [images, setImages] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState('grid-2x2');
  const [aspectPreset, setAspectPreset] = useState(ASPECT_PRESETS[0]);
  const [gap, setGap] = useState(16);
  const [borderRadius, setBorderRadius] = useState(12);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [exportFormat, setExportFormat] = useState('image/png');
  const [isProcessing, setIsProcessing] = useState(false);
  const [resultImage, setResultImage] = useState('');
  const [resultSize, setResultSize] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const fileInputRef = useRef(null);

  const activeLayoutObj = LAYOUT_PRESETS.find(l => l.id === selectedLayout) || LAYOUT_PRESETS[0];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setErrorMsg('');
    setResultImage('');

    const newImagePromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve({
            id: `img-${Date.now()}-${Math.random()}`,
            name: file.name,
            src: event.target.result
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(newImagePromises).then(newImgs => {
      setImages(prev => [...prev, ...newImgs].slice(0, 12)); // Cap at 12 images in queue
    });
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
    setResultImage('');
  };

  const clearAllImages = () => {
    setImages([]);
    setResultImage('');
    setErrorMsg('');
  };

  const handleRenderCollage = () => {
    if (images.length === 0) {
      setErrorMsg('Please upload at least one image to render a collage.');
      return;
    }

    setIsProcessing(true);
    setErrorMsg('');

    setTimeout(async () => {
      try {
        const { canvasW, canvasH } = aspectPreset;
        const canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;
        const ctx = canvas.getContext('2d');

        // Fill background
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvasW, canvasH);

        // Calculate layout bounding boxes
        const slots = getLayoutSlots(selectedLayout, canvasW, canvasH, gap);

        // Preload HTML Image elements
        const loadedImages = await Promise.all(
          images.slice(0, slots.length).map((imgObj) => {
            return new Promise((resolve) => {
              const img = new Image();
              img.src = imgObj.src;
              img.onload = () => resolve(img);
              img.onerror = () => resolve(null);
            });
          })
        );

        // Draw each slot
        slots.forEach((slot, index) => {
          const img = loadedImages[index % loadedImages.length]; // loop images if fewer than slots
          if (!img) return;

          ctx.save();
          ctx.beginPath();
          ctx.roundRect(slot.x, slot.y, slot.w, slot.h, borderRadius);
          ctx.clip();

          // Calculate object-fit cover
          const imgAspect = img.width / img.height;
          const slotAspect = slot.w / slot.h;
          let drawX = slot.x;
          let drawY = slot.y;
          let drawW = slot.w;
          let drawH = slot.h;

          if (imgAspect > slotAspect) {
            drawH = slot.h;
            drawW = img.width * (slot.h / img.height);
            drawX = slot.x - (drawW - slot.w) / 2;
          } else {
            drawW = slot.w;
            drawH = img.height * (slot.w / img.width);
            drawY = slot.y - (drawH - slot.h) / 2;
          }

          ctx.drawImage(img, drawX, drawY, drawW, drawH);
          ctx.restore();
        });

        const dataUrl = canvas.toDataURL(exportFormat, 0.92);
        setResultImage(dataUrl);

        const bytes = Math.round((dataUrl.length - 22) * 3 / 4);
        setResultSize(formatBytes(bytes));
        setIsProcessing(false);
      } catch (err) {
        setErrorMsg('Failed to process high-resolution collage rendering.');
        setIsProcessing(false);
      }
    }, 800);
  };

  const downloadResult = () => {
    if (!resultImage) return;
    const ext = exportFormat === 'image/png' ? 'png' : exportFormat === 'image/jpeg' ? 'jpg' : 'webp';
    const link = document.createElement('a');
    link.href = resultImage;
    link.download = `toolmetrico-collage-${selectedLayout}-${Date.now()}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    return parseFloat((bytes / k).toFixed(1)) + ' KB';
  };

  // Helper calculation for canvas slots
  const getLayoutSlots = (layoutId, W, H, gap) => {
    switch (layoutId) {
      case 'split-vertical': {
        const w = (W - 3 * gap) / 2;
        const h = H - 2 * gap;
        return [
          { x: gap, y: gap, w, h },
          { x: 2 * gap + w, y: gap, w, h },
        ];
      }
      case 'split-horizontal': {
        const w = W - 2 * gap;
        const h = (H - 3 * gap) / 2;
        return [
          { x: gap, y: gap, w, h },
          { x: gap, y: 2 * gap + h, w, h },
        ];
      }
      case 'collage-3': {
        const leftW = (W - 3 * gap) / 2;
        const rightW = (W - 3 * gap) / 2;
        const rightH = (H - 3 * gap) / 2;
        return [
          { x: gap, y: gap, w: leftW, h: H - 2 * gap },
          { x: 2 * gap + leftW, y: gap, w: rightW, h: rightH },
          { x: 2 * gap + leftW, y: 2 * gap + rightH, w: rightW, h: rightH },
        ];
      }
      case 'hero-top': {
        const topH = (H - 3 * gap) * 0.55;
        const bottomH = (H - 3 * gap) * 0.45;
        const bottomW = (W - 4 * gap) / 3;
        return [
          { x: gap, y: gap, w: W - 2 * gap, h: topH },
          { x: gap, y: 2 * gap + topH, w: bottomW, h: bottomH },
          { x: 2 * gap + bottomW, y: 2 * gap + topH, w: bottomW, h: bottomH },
          { x: 3 * gap + 2 * bottomW, y: 2 * gap + topH, w: bottomW, h: bottomH },
        ];
      }
      case 'collage-5': {
        const rowH = (H - 3 * gap) / 2;
        const topW = (W - 3 * gap) / 2;
        const bottomW = (W - 4 * gap) / 3;
        return [
          { x: gap, y: gap, w: topW, h: rowH },
          { x: 2 * gap + topW, y: gap, w: topW, h: rowH },
          { x: gap, y: 2 * gap + rowH, w: bottomW, h: rowH },
          { x: 2 * gap + bottomW, y: 2 * gap + rowH, w: bottomW, h: rowH },
          { x: 3 * gap + 2 * bottomW, y: 2 * gap + rowH, w: bottomW, h: rowH },
        ];
      }
      case 'grid-3x3': {
        const slotW = (W - 4 * gap) / 3;
        const slotH = (H - 4 * gap) / 3;
        const slots = [];
        for (let r = 0; r < 3; r++) {
          for (let c = 0; c < 3; c++) {
            slots.push({
              x: gap + c * (slotW + gap),
              y: gap + r * (slotH + gap),
              w: slotW,
              h: slotH,
            });
          }
        }
        return slots;
      }
      case 'grid-2x2':
      default: {
        const slotW = (W - 3 * gap) / 2;
        const slotH = (H - 3 * gap) / 2;
        return [
          { x: gap, y: gap, w: slotW, h: slotH },
          { x: 2 * gap + slotW, y: gap, w: slotW, h: slotH },
          { x: gap, y: 2 * gap + slotH, w: slotW, h: slotH },
          { x: 2 * gap + slotW, y: 2 * gap + slotH, w: slotW, h: slotH },
        ];
      }
    }
  };

  // Helper for live preview CSS Grid mappings
  const getPreviewGridStyle = (layoutId) => {
    switch (layoutId) {
      case 'split-vertical':
        return { display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr' };
      case 'split-horizontal':
        return { display: 'grid', gridTemplateColumns: '1fr', gridTemplateRows: '1fr 1fr' };
      case 'collage-3':
        return { display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' };
      case 'hero-top':
        return { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: '1.2fr 1fr' };
      case 'collage-5':
        return { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gridTemplateRows: '1fr 1fr' };
      case 'grid-3x3':
        return { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(3, 1fr)' };
      case 'grid-2x2':
      default:
        return { display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' };
    }
  };

  const getSlotItemStyle = (layoutId, index, rad) => {
    const base = {
      position: 'relative',
      borderRadius: `${rad}px`,
      overflow: 'hidden',
      background: 'rgba(15, 23, 42, 0.04)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      border: '1px dashed rgba(15, 23, 42, 0.12)'
    };
    switch (layoutId) {
      case 'collage-3':
        if (index === 0) return { ...base, gridRow: 'span 2' };
        return base;
      case 'hero-top':
        if (index === 0) return { ...base, gridColumn: 'span 3' };
        return base;
      case 'collage-5':
        if (index === 0 || index === 1) return { ...base, gridColumn: 'span 3' };
        return { ...base, gridColumn: 'span 2' };
      default:
        return base;
    }
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      <SEO 
        title="Free Photo Collage Maker - Create Photo Collage Online" 
        description="Create beautiful photo collages online for free. Combine multiple images into custom grid layouts, adjust borders, spacing, and background colors."
        keywords="photo collage maker, create photo collage online, picture collage generator, grid collage maker, combine photos online, photo layout editor"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Photo Collage Maker</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Photo Collage Maker Studio
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Combine multiple photos into beautiful, customizable grid layouts and creative canvases client-side.
        </p>
      </div>

      {/* Main Studio Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'start'
      }} className="collage-studio-grid">
        
        {/* Left Column: Layouts, Image Upload & Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          
          {/* Layout Presets Selection */}
          <div style={cardStyle}>
            <span style={labelStyle}>1. Select Grid Layout</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '10px' }}>
              {LAYOUT_PRESETS.map((p) => {
                const IconComponent = p.icon;
                const isSelected = selectedLayout === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => { setSelectedLayout(p.id); setResultImage(''); }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 8px',
                      background: isSelected ? 'var(--primary)' : 'rgba(15, 23, 42, 0.02)',
                      color: isSelected ? '#ffffff' : 'var(--text-primary)',
                      border: isSelected ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="layout-preset-btn"
                  >
                    <IconComponent size={22} style={{ color: isSelected ? '#ffffff' : 'var(--primary)' }} />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 700 }}>{p.name}</div>
                      <div style={{ fontSize: '0.65rem', opacity: isSelected ? 0.9 : 0.6 }}>{p.slots} Photos</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Image Queue Management */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={labelStyle}>2. Upload Image Queue ({images.length})</span>
              {images.length > 0 && (
                <button
                  onClick={clearAllImages}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ef4444',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Trash2 size={12} /> Clear Queue
                </button>
              )}
            </div>

            {/* Dropzone */}
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed rgba(236, 72, 153, 0.3)',
                background: 'var(--bg-secondary)',
                borderRadius: '16px',
                padding: '32px 20px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'var(--transition-smooth)'
              }}
              className="collage-dropzone"
            >
              <input 
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  background: 'rgba(236, 72, 153, 0.08)',
                  color: '#ec4899',
                  padding: '10px',
                  borderRadius: '50%',
                  display: 'inline-flex'
                }}>
                  <Upload size={20} />
                </div>
                <div>
                  <h5 style={{ margin: '0 0 2px 0', fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    Upload Photos for Collage
                  </h5>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Select multiple PNG, JPG, or WebP files
                  </p>
                </div>
              </div>
            </div>

            {/* Thumbnail Queue Grid */}
            {images.length > 0 && (
              <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '4px' }}>
                {images.map((img, i) => (
                  <div 
                    key={img.id}
                    style={{
                      position: 'relative',
                      width: '64px',
                      height: '64px',
                      flexShrink: 0,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid var(--border-color)',
                      boxShadow: 'var(--shadow-sm)'
                    }}
                  >
                    <img src={img.src} alt={img.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button
                      onClick={() => removeImage(img.id)}
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        background: 'rgba(0,0,0,0.65)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: '10px'
                      }}
                    >
                      &times;
                    </button>
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'rgba(0,0,0,0.5)',
                      color: '#ffffff',
                      fontSize: '9px',
                      textAlign: 'center',
                      padding: '1px'
                    }}>
                      #{i+1}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sliders and Configurations */}
          <div style={cardStyle}>
            <span style={labelStyle}>3. Canvas Settings & Spacing</span>

            {/* Aspect Ratio Presets */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 700 }}>Canvas Aspect Ratio</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                {ASPECT_PRESETS.map((ap) => (
                  <button
                    key={ap.id}
                    onClick={() => { setAspectPreset(ap); setResultImage(''); }}
                    style={{
                      padding: '8px 4px',
                      borderRadius: '8px',
                      border: aspectPreset.id === ap.id ? '1.5px solid #ec4899' : '1px solid var(--border-color)',
                      background: aspectPreset.id === ap.id ? 'rgba(236, 72, 153, 0.05)' : '#ffffff',
                      color: aspectPreset.id === ap.id ? '#ec4899' : 'var(--text-secondary)',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {ap.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Gap Slider */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                <span>Photo Gap Spacing</span>
                <span>{gap}px</span>
              </div>
              <input 
                type="range"
                min="0"
                max="60"
                value={gap}
                onChange={(e) => { setGap(parseInt(e.target.value)); setResultImage(''); }}
                style={{ width: '100%', accentColor: '#ec4899' }}
              />
            </div>

            {/* Border Radius Slider */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                <span>Rounded Corners (Radius)</span>
                <span>{borderRadius}px</span>
              </div>
              <input 
                type="range"
                min="0"
                max="60"
                value={borderRadius}
                onChange={(e) => { setBorderRadius(parseInt(e.target.value)); setResultImage(''); }}
                style={{ width: '100%', accentColor: '#ec4899' }}
              />
            </div>

            {/* Background Color Picker */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 700 }}>Background Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input 
                  type="color"
                  value={bgColor}
                  onChange={(e) => { setBgColor(e.target.value); setResultImage(''); }}
                  style={{
                    width: '40px',
                    height: '40px',
                    padding: 0,
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {PRESET_COLORS.map((col) => (
                    <button 
                      key={col}
                      onClick={() => { setBgColor(col); setResultImage(''); }}
                      style={{ 
                        width: '26px', 
                        height: '26px', 
                        borderRadius: '6px', 
                        background: col, 
                        border: bgColor === col ? '2px solid #ec4899' : '1px solid var(--border-color)',
                        cursor: 'pointer'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Export Format */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 700 }}>Export Image Format</label>
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer'
                }}
              >
                <option value="image/png">PNG Format (Lossless)</option>
                <option value="image/jpeg">JPG Format (Web Standard)</option>
                <option value="image/webp">WEBP Format (Compressed)</option>
              </select>
            </div>
          </div>

          {/* Action Render Button */}
          <button
            onClick={handleRenderCollage}
            disabled={isProcessing}
            style={{
              height: '48px',
              border: 'none',
              background: 'linear-gradient(90deg, #ec4899 0%, #8b5cf6 100%)',
              color: '#ffffff',
              borderRadius: '12px',
              fontSize: '0.95rem',
              fontWeight: 700,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'var(--transition-smooth)'
            }}
            className="render-collage-btn"
          >
            {isProcessing ? <RefreshCw size={16} className="spinner" /> : <Sparkles size={16} />}
            {isProcessing ? 'Generating high-res collage...' : 'Render Collage Canvas'}
          </button>

          {errorMsg && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.15)',
              padding: '14px 20px',
              borderRadius: '12px',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '0.85rem',
              fontWeight: 600
            }}>
              <AlertCircle size={16} /> {errorMsg}
            </div>
          )}

        </div>

        {/* Right Column: Live Interactive Preview & Result */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          
          {/* Live Preview Container */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={labelStyle}>Live Grid Layout Preview</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 700 }}>
                {aspectPreset.label} — {activeLayoutObj.slots} Slots
              </span>
            </div>

            {/* CSS Grid live canvas emulator */}
            <div style={{
              width: '100%',
              aspectRatio: aspectPreset.value,
              background: bgColor,
              padding: `${gap}px`,
              gap: `${gap}px`,
              borderRadius: '16px',
              border: '1px solid var(--border-color)',
              boxSizing: 'border-box',
              ...getPreviewGridStyle(selectedLayout),
              transition: 'all 0.3s ease'
            }}>
              {Array.from({ length: activeLayoutObj.slots }).map((_, index) => {
                const imgObj = images[index % images.length]; // loop available images
                const slotStyle = getSlotItemStyle(selectedLayout, index, borderRadius);
                
                return (
                  <div key={index} style={slotStyle}>
                    {imgObj ? (
                      <img 
                        src={imgObj.src} 
                        alt={`Slot ${index+1}`} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                    ) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        color: 'var(--text-muted)',
                        padding: '12px',
                        textAlign: 'center'
                      }}>
                        <ImageIcon size={20} style={{ opacity: 0.5 }} />
                        <span style={{ fontSize: '0.7rem', fontWeight: 700 }}>Slot #{index+1}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
              💡 Adjust spacing, rounded corners, or background colors on the left to see instant layout updates.
            </div>
          </div>

          {/* Rendered Image Asset Card */}
          {resultImage && (
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: 'var(--shadow-md)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ ...labelStyle, color: '#059669' }}>High Resolution Collage Rendered</span>
                <span style={{ fontSize: '0.65rem', background: 'rgba(5, 150, 105, 0.08)', color: '#059669', padding: '2px 8px', borderRadius: '4px', fontWeight: 800 }}>
                  Ready
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-color)',
                  aspectRatio: aspectPreset.value,
                  background: 'var(--bg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img 
                    src={resultImage} 
                    alt="Collage Success Output" 
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  <span>File Size: <strong style={{ color: '#059669' }}>{resultSize}</strong></span>
                  <span>Resolution: <strong>{aspectPreset.canvasW} x {aspectPreset.canvasH} px</strong></span>
                </div>

                <AdToast message={toast} />
                <button
                  onClick={withAd('photo-collage', downloadResult)}
                  style={{
                    height: '42px',
                    border: 'none',
                    background: '#059669',
                    color: '#ffffff',
                    borderRadius: '10px',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'var(--transition-smooth)'
                  }}
                  className="download-collage-btn"
                >
                  <Download size={14} /> Download Final Collage
                </button>
              </div>
            </div>
          )}

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .collage-studio-grid {
            grid-template-columns: 420px 1fr !important;
          }
        }

        .layout-preset-btn:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-sm);
        }

        .collage-dropzone:hover {
          border-color: #ec4899 !important;
          background: rgba(236, 72, 153, 0.02) !important;
        }

        .render-collage-btn:hover {
          opacity: 0.95;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(236, 72, 153, 0.25) !important;
        }

        .download-collage-btn:hover {
          background: #047857 !important;
          transform: translateY(-1px);
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* SEO & Related Tools Footprint */}
      <div style={{ marginTop: '40px' }}>
        <AdUnit />
        <ToolPageExtras toolId="photo-collage" />
      </div>
    </div>
  );
};

// Common UI Styling Rules
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxShadow: 'var(--shadow-sm)'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

export default PhotoCollageMaker;
